import React from "react";
import { CheckCircle, AlertCircle, Users, TrendingUp, TrendingDown, Landmark, Loader2 } from "lucide-react";

const StepReviewProcess = ({
  previewData,
  selectedEmployees,
  processingPayroll,
  modalStyles,
  currentTheme,
  t,
  language = "en",
}) => {
  const c = currentTheme.colors;
  const L = (ar, en) => (language === "ar" ? ar : en);
  const money = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const stats = previewData
    ? [
        {
          label: t("employees"),
          value: previewData.totalEmployees,
          icon: Users,
          color: c.primary,
          soft: c.primarySoft,
        },
        {
          label: t("grossTotal"),
          value: money(previewData.totalGross),
          unit: t("currency"),
          icon: TrendingUp,
          color: c.info,
          soft: c.infoSoft,
        },
        {
          label: t("deductions"),
          value: money(previewData.totalDeductions),
          unit: t("currency"),
          icon: TrendingDown,
          color: c.danger,
          soft: c.dangerSoft,
        },
        {
          label: t("taxInsurance"),
          value: money(previewData.taxTotal + previewData.insuranceTotal),
          unit: t("currency"),
          icon: Landmark,
          color: c.warning,
          soft: c.warningSoft,
        },
      ]
    : [];

  const th = {
    padding: "12px 20px",
    fontSize: "12.5px",
    fontWeight: 700,
    color: c.textMuted,
    textAlign: "end",
    background: c.surfaceSubtle,
    borderBottom: `1px solid ${c.borderLight}`,
    position: "sticky",
    top: 0,
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "13px 20px",
    fontSize: "14.5px",
    textAlign: "end",
    borderBottom: `1px solid ${c.borderLight}`,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  };

  return (
    <div className="pp-section" style={{ ...modalStyles.formSection, marginBottom: 0 }}>
      <div style={{ marginBottom: "24px" }}>
        <h3 style={modalStyles.sectionTitle}>
          <CheckCircle size={20} style={{ color: c.primary }} />
          {t("reviewProcess")}
        </h3>
        <p style={modalStyles.sectionSubtitle}>
          {L(
            "راجع الإجماليات قبل تأكيد المعالجة.",
            "Review the totals below before confirming the payroll run.",
          )}
        </p>
      </div>

      {!previewData && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            padding: "48px 24px",
            color: c.textMuted,
            fontSize: "14.5px",
          }}
        >
          <Loader2 size={18} className="pp-spin" />
          {L("جارٍ حساب المعاينة…", "Calculating preview…")}
          <style>{`@keyframes pp-spin { to { transform: rotate(360deg); } } .pp-spin { animation: pp-spin 1s linear infinite; }`}</style>
        </div>
      )}

      {previewData && (
        <>
          {/* ── KPI cards ─────────────────────────────────────────────── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
              gap: "14px",
            }}
          >
            {stats.map(({ label, value, unit, icon: Icon, color, soft }) => (
              <div
                key={label}
                style={{
                  padding: "18px 20px",
                  borderRadius: "12px",
                  border: `1px solid ${c.borderLight}`,
                  background: c.surface,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <span
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "9px",
                      background: soft,
                      color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={17} />
                  </span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: c.textSecondary }}>{label}</span>
                </div>
                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: c.textPrimary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {value}
                  {unit && (
                    <span style={{ fontSize: "13px", fontWeight: 600, color: c.textMuted, marginInlineStart: "6px" }}>
                      {unit}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ── Net total ─────────────────────────────────────────────── */}
          <div
            style={{
              ...modalStyles.previewTotal,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: "15px", fontWeight: 600, opacity: 0.9 }}>{t("netTotal")}</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {money(previewData.totalNet)}{" "}
              <span style={{ fontSize: "15px", fontWeight: 600, opacity: 0.85 }}>{t("currency")}</span>
            </span>
          </div>

          {/* ── Employee breakdown ────────────────────────────────────── */}
          <div style={{ marginTop: "28px" }}>
            <div style={{ fontSize: "15px", fontWeight: 700, color: c.textPrimary, marginBottom: "12px" }}>
              {t("employeeBreakdown")}
            </div>
            <div
              className="pp-scroll"
              style={{
                border: `1px solid ${c.borderLight}`,
                borderRadius: "12px",
                overflow: "auto",
                maxHeight: "340px",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px" }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: "start" }}>{L("الموظف", "Employee")}</th>
                    <th style={th}>{L("الإجمالي", "Gross")}</th>
                    <th style={th}>{t("deductions")}</th>
                    <th style={th}>{L("الصافي", "Net")}</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.employees.map((emp, index) => (
                    <tr key={index}>
                      <td style={{ ...td, textAlign: "start", fontWeight: 600, color: c.textPrimary, fontVariantNumeric: "normal" }}>
                        {emp.name}
                      </td>
                      <td style={{ ...td, color: c.textSecondary }}>{money(emp.gross)}</td>
                      <td style={{ ...td, color: c.danger }}>
                        {emp.deductions ? `−${money(emp.deductions)}` : money(0)}
                      </td>
                      <td style={{ ...td, fontWeight: 700, color: c.textPrimary }}>{money(emp.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Warning ───────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          background: c.warningSoft,
          border: `1px solid ${c.warning}33`,
          padding: "14px 16px",
          borderRadius: "10px",
          marginTop: "20px",
          color: c.textSecondary,
          fontSize: "14px",
          lineHeight: 1.5,
        }}
      >
        <AlertCircle size={18} style={{ color: c.warning, flexShrink: 0, marginTop: "1px" }} />
        <span>
          <strong style={{ color: c.textPrimary }}>{t("important")}:</strong>{" "}
          {t("processPayrollWarning").replace("{count}", selectedEmployees.length)}
        </span>
      </div>
    </div>
  );
};

export default StepReviewProcess;
